<template>
  <div class="ocr-report-header-keywords">
    <div class="ocr-report-map-toolbar">
      <v-btn size="small" color="primary" variant="flat" :loading="saving" :disabled="!pendingAssignments.length" @click="saveKeywords">
        Save keywords &amp; map
      </v-btn>
      <v-switch v-model="editTerms" density="compact" hide-details label="Edit term text" />
    </div>
    <div v-if="loading" class="ocr-empty-cell">Loading page keyword rows…</div>
    <div v-else-if="!rows.length" class="ocr-empty-cell">
      No keyword rows on page {{ page + 1 }}. Process report with OCR text extraction enabled.
    </div>
    <div v-else class="ocr-header-keyword-list">
      <div v-for="row in rows" :key="row.row_index" class="ocr-header-keyword-card">
        <div class="ocr-header-keyword-value">{{ row.value }}</div>
        <input
          v-if="editTerms"
          v-model="row.terms_text"
          class="ocr-editable-grid-input"
          type="text"
          @input="syncTermsFromText(row)"
        />
        <div class="ocr-field">
          <label class="profile-menu-label">Keyword terms</label>
          <v-select
            v-model="row.selected_terms"
            :items="termOptions(row)"
            :menu-props="{ contentClass: 'ocr-select-menu' }"
            multiple
            chips
            closable-chips
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="onTermsChange(row)"
          />
        </div>
        <div v-if="row.selected_terms?.length" class="ocr-header-keyword-map-row">
          <div class="ocr-field">
            <label class="profile-menu-label">Table</label>
            <v-select
              v-model="row.selected_table"
              :items="tableTypes"
              :menu-props="{ contentClass: 'ocr-select-menu' }"
              density="compact"
              variant="outlined"
              hide-details
              class="ocr-report-column-select"
              @update:model-value="loadFields(row)"
            />
          </div>
          <div class="ocr-field">
            <label class="profile-menu-label">Field</label>
            <v-select
              v-model="row.selected_field"
              :items="row.field_options || []"
              :menu-props="{ contentClass: 'ocr-select-menu' }"
              density="compact"
              variant="outlined"
              hide-details
              class="ocr-report-column-select"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";

export default {
  name: "OcrReportHeaderKeywords",
  props: {
    filename: { type: String, default: null },
    tableIndex: { type: Number, default: null },
  },
  emits: ["updated", "status"],
  data() {
    return {
      loading: false,
      saving: false,
      page: 0,
      tableTypes: [],
      keywordDefinitions: [],
      rows: [],
      editTerms: false,
      fieldCache: {},
    };
  },
  computed: {
    pendingAssignments() {
      return this.rows
        .filter((r) => r.selected_terms?.length && r.selected_table && r.selected_field)
        .map((r) => ({
          keyword: r.selected_terms.join(" "),
          table: r.selected_table,
          data_model_field: r.selected_field,
        }));
    },
  },
  watch: {
    filename: { handler() { this.load(); } },
    tableIndex: { handler() { this.load(); } },
  },
  mounted() {
    this.load();
  },
  methods: {
    termOptions(row) {
      if (this.editTerms && row.terms_text) {
        return row.terms_text.split(",").map((t) => t.trim()).filter(Boolean);
      }
      return row.terms || [];
    },
    syncTermsFromText(row) {
      row.terms = this.termOptions(row);
    },
    inferTableField(row) {
      const first = row.selected_terms?.[0];
      if (!first) return;
      const hit = this.keywordDefinitions.find(
        (d) => String(d.Keyword || d.keyword || "").toLowerCase() === String(first).toLowerCase(),
      );
      if (hit) {
        row.selected_table = hit.Table || hit.table || row.selected_table;
        row.selected_field = hit.DataModelField || hit.dataModelField || row.selected_field;
        this.loadFields(row);
      }
    },
    onTermsChange(row) {
      this.inferTableField(row);
    },
    async loadFields(row) {
      const table = row.selected_table;
      if (!table) {
        row.field_options = [];
        return;
      }
      if (this.fieldCache[table]) {
        row.field_options = this.fieldCache[table];
        return;
      }
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/meta/header-fields/${encodeURIComponent(table)}`,
        );
        const fields = data.fields || [];
        this.fieldCache[table] = fields;
        row.field_options = fields;
      } catch {
        row.field_options = [];
      }
    },
    async load() {
      this.rows = [];
      if (!this.filename || this.tableIndex == null) return;
      this.loading = true;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/headers/keywords`,
        );
        this.page = data.page ?? 0;
        this.tableTypes = data.table_types || [];
        this.keywordDefinitions = data.keyword_definitions || [];
        this.rows = (data.rows || []).map((r) => ({
          ...r,
          terms_text: r.terms_text || (r.terms || []).join(", "),
          selected_terms: [],
          selected_table: data.table_type || this.tableTypes[0] || "",
          selected_field: "",
          field_options: [],
        }));
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Could not load keyword rows.") });
      } finally {
        this.loading = false;
      }
    },
    async saveKeywords() {
      if (!this.pendingAssignments.length) return;
      this.saving = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/headers/keywords`,
          { assignments: this.pendingAssignments },
        );
        this.$emit("status", {
          type: "success",
          message: `Saved ${data.keywords_saved || 0} keyword mapping(s).`,
        });
        this.$emit("updated", data);
        await this.load();
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Save keywords failed.") });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
